import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, CreditCard, Building2, Wifi } from "lucide-react";

const MODE_COLORS = {
  credit_card: "bg-blue-500",
  debit_card: "bg-green-500",
  net_banking: "bg-orange-500",
  upi: "bg-purple-500",
  corporate_credit_card: "bg-indigo-500",
  commercial_card: "bg-cyan-500",
  prepaid_card: "bg-pink-500",
  pay_later: "bg-amber-500",
  emi_credit_card: "bg-rose-500",
  upi_credit_card: "bg-violet-500",
  upi_prepaid_card: "bg-fuchsia-500",
  upi_credit_line: "bg-teal-500",
  wallet: "bg-emerald-500",
  qr: "bg-lime-500",
  other: "bg-gray-400",
};

export default function AdminPaymentAnalytics() {
  const [data, setData] = useState(null);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/payment-analytics"),
      api.get("/admin/payment-analytics/modes"),
    ])
      .then(([anaRes, modesRes]) => {
        setData(anaRes.data);
        setModes(modesRes.data.modes || []);
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const totalVolume = data?.totals?.total_volume || 0;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="payment-analytics-page">
        <span className="overline">Admin · Marketing & Analytics</span>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-2 flex items-center gap-2">
          <CreditCard className="h-7 w-7 text-primary" strokeWidth={1.5} />
          Payment Mode Analytics
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl text-sm">
          Granular breakdown of how customers pay — by card type, network, bank, and UPI variant.
          Every unique mode is auto-discovered from gateway responses.
        </p>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading analytics...
          </div>
        ) : (
          <div className="space-y-8 mt-6">
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="surface-card p-5" data-testid="kpi-total">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total settled txns</div>
                <div className="font-display text-2xl font-bold mt-1">{(data?.totals?.total_txns || 0).toLocaleString()}</div>
              </div>
              <div className="surface-card p-5" data-testid="kpi-with-paymode">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">With pay mode data</div>
                <div className="font-display text-2xl font-bold mt-1">{(data?.totals?.with_paymode || 0).toLocaleString()}</div>
              </div>
              <div className="surface-card p-5" data-testid="kpi-volume">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total volume</div>
                <div className="font-display text-2xl font-bold mt-1">{formatINR(totalVolume)}</div>
              </div>
            </div>

            {/* By Mode Type */}
            <section className="surface-card overflow-hidden" data-testid="section-by-mode">
              <div className="px-5 py-3 bg-muted/40 border-b border-border">
                <h2 className="font-display text-lg font-semibold">By payment mode</h2>
              </div>
              <div className="p-5">
                {(data?.by_mode || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No payment mode data yet. Modes are captured when transactions settle via gateway webhooks.</p>
                ) : (
                  <div className="space-y-3">
                    {(data?.by_mode || []).map((m) => {
                      const pct = totalVolume > 0 ? (m.volume / totalVolume * 100).toFixed(1) : 0;
                      return (
                        <div key={m.mode_type} className="flex items-center gap-3" data-testid={`mode-${m.mode_type}`}>
                          <div className={`h-3 w-3 rounded-full ${MODE_COLORS[m.mode_type] || "bg-gray-400"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{m.label}</span>
                              <span className="text-sm font-mono">{formatINR(m.volume)}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${MODE_COLORS[m.mode_type] || "bg-gray-400"}`}
                                  style={{ width: `${Math.max(pct, 1)}%` }}
                                />
                              </div>
                              <span className="text-[10px] font-mono text-muted-foreground w-14 text-right">{pct}% · {m.count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* By Card Network + By Bank side by side */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Card Networks */}
              <section className="surface-card overflow-hidden" data-testid="section-by-network">
                <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center gap-2">
                  <Wifi className="h-4 w-4 text-primary" strokeWidth={1.5} />
                  <h2 className="font-display font-semibold">Card networks</h2>
                </div>
                <div className="p-5">
                  {(data?.by_network || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No card network data yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {(data?.by_network || []).map((n) => (
                        <div key={n.network} className="flex items-center justify-between px-3 py-2 rounded border border-border/50">
                          <span className="text-sm font-medium capitalize">{n.network}</span>
                          <div className="text-right">
                            <span className="text-sm font-mono">{formatINR(n.volume)}</span>
                            <span className="ml-2 text-[10px] text-muted-foreground">{n.count} txns</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* Banks */}
              <section className="surface-card overflow-hidden" data-testid="section-by-bank">
                <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" strokeWidth={1.5} />
                  <h2 className="font-display font-semibold">Top banks</h2>
                </div>
                <div className="p-5">
                  {(data?.by_bank || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No bank data yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {(data?.by_bank || []).map((b) => (
                        <div key={b.bank} className="flex items-center justify-between px-3 py-2 rounded border border-border/50">
                          <span className="text-sm truncate max-w-[60%]">{b.bank}</span>
                          <div className="text-right shrink-0">
                            <span className="text-sm font-mono">{formatINR(b.volume)}</span>
                            <span className="ml-2 text-[10px] text-muted-foreground">{b.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* Unique Modes Table */}
            <section className="surface-card overflow-hidden" data-testid="section-unique-modes">
              <div className="px-5 py-3 bg-muted/40 border-b border-border">
                <h2 className="font-display text-lg font-semibold">Unique pay modes discovered</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Every distinct combination from gateway responses. Use for whitelist configuration.</p>
              </div>
              {modes.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No payment modes discovered yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="p-3 pl-5">Mode type</th>
                        <th className="p-3">Sub-type / PG code</th>
                        <th className="p-3">Card network</th>
                        <th className="p-3">Bank</th>
                        <th className="p-3 text-right pr-5">Usage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modes.map((m, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0 hover:bg-accent/30">
                          <td className="p-3 pl-5">
                            <div className="flex items-center gap-2">
                              <div className={`h-2.5 w-2.5 rounded-full ${MODE_COLORS[m.mode_type] || "bg-gray-400"}`} />
                              <span className="font-medium">{m.label}</span>
                            </div>
                          </td>
                          <td className="p-3 font-mono text-xs text-muted-foreground">{m.mode_subtype || "—"}</td>
                          <td className="p-3 capitalize">{m.card_network || "—"}</td>
                          <td className="p-3 text-xs">{m.bank_name || "—"}</td>
                          <td className="p-3 text-right pr-5 font-mono">{m.usage_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
